import { keyframes, styled } from '@mui/material'
import BubbleTail from '../../../assets/icons/emotes/bubble-tail.svg?react'
import { useEmotesMuted } from '../../../sharedComponents/src/settings/emotesMuted'
import { useStoreState } from '../../../store/hooks'

// Emote content is plain text from the server; a leading emoji is styled apart from the label.
const LEADING_EMOJI =
    /^((?:\p{Regional_Indicator}{2}|\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?(?:‍\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?)*)+)\s*/u

export const splitEmote = (content: string) => {
    const match = content.match(LEADING_EMOJI)
    return match ? { emoji: match[1], label: content.slice(match[0].length) } : { emoji: '', label: content }
}

// Figma "Emote bubble": `beside` = variant Either (left of the player-bar avatar, tail pointing at it),
// `above` = variant Self (over the result-dialog avatar, tail at the bottom right).
export type EmoteBubblePlacement = 'beside' | 'above'

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  to { opacity: 1; transform: scale(1); }
`

const Anchor = styled('div')<{ placement: EmoteBubblePlacement }>(({ placement }) => ({
    position: 'absolute',
    zIndex: 4,
    pointerEvents: 'none',
    ...(placement === 'beside'
        ? { right: 'calc(100% + 6px)', top: '50%', transform: 'translateY(-50%)' }
        : { bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' }),
}))

const Bubble = styled('div')<{ placement: EmoteBubblePlacement }>(({ placement }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '6px',
    boxSizing: 'border-box',
    height: placement === 'beside' ? '44px' : '26px',
    padding: placement === 'beside' ? '7px 16px' : '7px 12px 7px 10px',
    maxWidth: placement === 'beside' ? 'min(240px, calc(100vw - 120px))' : '180px',
    borderRadius: '8px',
    backgroundColor: '#F4F4F4',
    color: '#293E56',
    fontFamily: 'Space Grotesk',
    whiteSpace: 'nowrap',
    filter: 'drop-shadow(0px 2px 0px rgba(0, 0, 0, 0.4))',
    transformOrigin: placement === 'beside' ? 'right center' : 'right bottom',
    animation: `${popIn} 160ms ease-out`,
    fontFeatureSettings: '"salt" 1, "ss02" 1, "liga" 0',
}))

const Emoji = styled('span')({
    flexShrink: 0,
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: 'normal',
})

const Label = styled('span')<{ placement: EmoteBubblePlacement }>(({ placement }) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: placement === 'beside' ? '16px' : '14px',
    fontWeight: placement === 'beside' ? 300 : 400,
    lineHeight: placement === 'beside' ? 1.4 : 1,
}))

const Tail = styled('div')<{ placement: EmoteBubblePlacement }>(({ placement }) => ({
    position: 'absolute',
    display: 'flex',
    width: '8px',
    height: '8px',
    ...(placement === 'beside'
        ? { right: '-8px', top: '50%', transform: 'translateY(-50%)' }
        : { right: '8px', bottom: '-8px' }),
    svg: { transform: 'rotate(90deg)' },
}))

export const EmoteBubble = ({ content, placement }: { content: string; placement: EmoteBubblePlacement }) => {
    const { emoji, label } = splitEmote(content)

    return (
        <Anchor placement={placement}>
            <Bubble placement={placement} role="status">
                {emoji && <Emoji>{emoji}</Emoji>}
                {label && <Label placement={placement}>{label}</Label>}
                <Tail placement={placement}>
                    <BubbleTail />
                </Tail>
            </Bubble>
        </Anchor>
    )
}

// The latest emote of one player; the parent must be `position: relative` around the avatar.
export const PlayerEmote = ({ playerId, placement }: { playerId?: string; placement: EmoteBubblePlacement }) => {
    const bubble = useStoreState((state) =>
        playerId && state.emotes.matchId === state.gameView.gameData?.matchId
            ? state.emotes.bubbles[playerId]
            : undefined,
    )
    const muted = useEmotesMuted()

    if (!bubble || (muted && bubble.fromOpponent)) return null

    return <EmoteBubble key={bubble.id} content={bubble.content} placement={placement} />
}
